import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// import Board from "../../chessBoard3/components/Board";
import Board from "../../chessBoard/components/Board";
import { Chess } from "@chesshotel/chess";
import { api } from "@/services/api";
import { useUser } from "@/redux/user";
import {
  getGuestLessonProgress,
  saveGuestLessonProgress,
} from "@/modules/lessons/guestLessonProgress";

// import { Chess } from "./chess";
import { convertMoves } from "../../util/moves";
import { cn } from "@/lib/utils";
import { uci2move } from "@chesshotel/gameReview";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSettings } from "@/redux/settings";
import MoveIcon from "@/components/common/move-icon";
import { MoveType } from "@chesshotel/types";

import {
  FaStepBackward,
  FaStepForward,
  FaArrowLeft,
  FaChevronDown,
  FaTrophy,
  FaLock,
} from "react-icons/fa";
import Link from "next/link";

import useTranslation from "next-translate/useTranslation";

// import Puzzle from "@/pages/puzzle";
// const chess = Chess();

enum LessonState {
  OPENING,
  IN_PROGRESS,
  DONE,
  CHALLENGE,
  CHALLENGE_DONE,
}

type StartMode = "practice" | "challenge";

type LessonProgressResponse = {
  completedLines?: unknown;
  completedChallengeLines?: unknown;
  practiceLineProgress?: unknown;
  challengeLineProgress?: unknown;
  challengeCompleted?: boolean;
};

type PersistProgressArgs = {
  completedLinesOverride?: number[];
  completedChallengeLinesOverride?: number[];
  practiceLineProgressOverride?: number[];
  challengeLineProgressOverride?: number[];
  challengeCompletedOverride?: boolean;
};

const normalizeCompletedIndices = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => Number(entry))
          .filter((entry) => Number.isInteger(entry) && entry >= 0),
      ),
    );
  }

  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return Array.from(
        new Set(
          parsed
            .map((entry) => Number(entry))
            .filter((entry) => Number.isInteger(entry) && entry >= 0),
        ),
      );
    }
  } catch (_error) {
    // fall through to CSV parsing
  }

  return Array.from(
    new Set(
      value
        .split(",")
        .map((entry) => Number(entry.trim()))
        .filter((entry) => Number.isInteger(entry) && entry >= 0),
    ),
  );
};

const parseNumberArray = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => Number(entry))
      .filter((entry) => Number.isInteger(entry) && entry >= 0);
  }

  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry) => Number(entry))
        .filter((entry) => Number.isInteger(entry) && entry >= 0);
    }
  } catch (_error) {
    // fall through to CSV parsing
  }

  return value
    .split(",")
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isInteger(entry) && entry >= 0);
};

const normalizeLineProgress = (value: unknown): number[] => {
  const normalized = parseNumberArray(value);

  let lastNonZeroIndex = normalized.length - 1;
  while (lastNonZeroIndex >= 0 && normalized[lastNonZeroIndex] === 0) {
    lastNonZeroIndex -= 1;
  }

  if (lastNonZeroIndex < 0) {
    return [];
  }

  return normalized.slice(0, lastNonZeroIndex + 1);
};

const getPlayerMoveCountFromPly = (
  plyCount: number,
  playingWhite: boolean,
): number => {
  if (plyCount <= 0) {
    return 0;
  }

  return playingWhite ? Math.ceil(plyCount / 2) : Math.floor(plyCount / 2);
};

const getLinePlayerMoveTotals = (lessonLines: any[], playingWhite: boolean) => {
  return (lessonLines || []).map((lessonLine) => {
    if (!lessonLine?.moves) {
      return 0;
    }

    try {
      const chess = new Chess();
      chess.loadPgn(lessonLine.moves);
      const lineMoves = chess.history({ verbose: true });
      return getPlayerMoveCountFromPly(lineMoves.length, playingWhite);
    } catch (_error) {
      return Math.max((lessonLine?.comments?.length ?? 1) - 1, 0);
    }
  });
};

const clampPercent = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(Math.max(value, 0), 100);
};

function LessonProgressBar({
  value = 0,
  className = "",
  indicatorClassName = "bg-primary",
}: {
  value?: number;
  className?: string;
  indicatorClassName?: string;
}) {
  const clampedValue = clampPercent(value);

  return (
    <div
      className={`relative h-2 w-full overflow-hidden rounded-full bg-athens-gray-200 ${className}`.trim()}
    >
      <div
        className={`h-full w-full flex-1 transition-all ${indicatorClassName}`.trim()}
        style={{ transform: `translateX(-${100 - clampedValue}%)` }}
      />
    </div>
  );
}

const getLineMoveSignature = (lessonLine: any): string[] => {
  if (!lessonLine?.moves) {
    return [];
  }

  try {
    const chess = new Chess();
    chess.loadPgn(lessonLine.moves);
    return chess
      .history({ verbose: true })
      .map((move) => `${move.from}${move.to}${move.promotion ?? ""}`);
  } catch (_error) {
    return [];
  }
};

const getCommonPrefixLength = (left: string[], right: string[]) => {
  const minLength = Math.min(left.length, right.length);
  let index = 0;

  while (index < minLength && left[index] === right[index]) {
    index += 1;
  }

  return index;
};

const buildAlignedChallengeLines = (
  practiceLines: any[],
  configuredChallengeLines: any[],
) => {
  if (configuredChallengeLines.length === 0) {
    return [];
  }

  if (practiceLines.length === 0) {
    return configuredChallengeLines;
  }

  const practiceSignatures = practiceLines.map(getLineMoveSignature);
  const challengeSignatures =
    configuredChallengeLines.map(getLineMoveSignature);
  const alignedChallengeLines = [...practiceLines];
  const usedPracticeIndices = new Set<number>();

  configuredChallengeLines.forEach((challengeLine, challengeLineIdx) => {
    const challengeSignature = challengeSignatures[challengeLineIdx];
    let bestPracticeIdx = -1;
    let bestScore = -1;

    practiceSignatures.forEach((practiceSignature, practiceIdx) => {
      if (usedPracticeIndices.has(practiceIdx)) {
        return;
      }

      const score = getCommonPrefixLength(
        challengeSignature,
        practiceSignature,
      );
      if (score > bestScore) {
        bestScore = score;
        bestPracticeIdx = practiceIdx;
      }
    });

    if (bestPracticeIdx === -1) {
      return;
    }

    usedPracticeIndices.add(bestPracticeIdx);
    alignedChallengeLines[bestPracticeIdx] = challengeLine;
  });

  return alignedChallengeLines;
};

const setLineProgressAtIndex = (
  sourceProgress: number[],
  targetLineIndex: number,
  moveCount: number,
) => {
  const normalized = normalizeLineProgress(sourceProgress);
  const nextProgress = [...normalized];
  const currentValue = nextProgress[targetLineIndex] ?? 0;
  nextProgress[targetLineIndex] = Math.max(currentValue, moveCount);
  return normalizeLineProgress(nextProgress);
};

const applyCompletedLinesToProgress = (
  sourceProgress: number[],
  completedLineIndices: number[],
  lineMoveTotals: number[],
) => {
  const nextProgress = [...normalizeLineProgress(sourceProgress)];

  completedLineIndices.forEach((lineIdx) => {
    const lineTotal = lineMoveTotals[lineIdx] ?? 0;
    if (lineTotal > 0) {
      nextProgress[lineIdx] = Math.max(nextProgress[lineIdx] ?? 0, lineTotal);
    }
  });

  return normalizeLineProgress(nextProgress);
};

const findNextIncompleteLine = (
  lineProgress: number[],
  lineMoveTotals: number[],
) => {
  return lineMoveTotals.findIndex((lineTotal, lineIdx) => {
    const completedMoves = Math.min(lineProgress[lineIdx] ?? 0, lineTotal);
    return completedMoves < lineTotal;
  });
};

const areAllLinesCompleted = (
  lineProgress: number[],
  completedLineIndices: number[],
  lineMoveTotals: number[],
) => {
  if (!lineMoveTotals || lineMoveTotals.length === 0) {
    return false;
  }

  const completedSet = new Set(completedLineIndices);

  return lineMoveTotals.every((lineTotal, lineIdx) => {
    if (completedSet.has(lineIdx)) {
      return true;
    }

    if (lineTotal <= 0) {
      return true;
    }

    return (lineProgress[lineIdx] ?? 0) >= lineTotal;
  });
};

const getMoveIndexForResume = (
  playingWhite: boolean,
  playerMovesDone: number,
) => {
  if (playerMovesDone <= 0) {
    return 0;
  }

  return playingWhite ? playerMovesDone * 2 : playerMovesDone * 2 + 1;
};
// const startFen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

export default function LessonBoard(props) {
  const { t } = useTranslation("common");
  const {
    playingWhite,
    lines,
    introText,
    title,
    challengeFen,
    challengeOpeningLastMove,
    challengeLines,
    lessonId,
  } = props;

  const user = useUser();
  const settings = useSettings();
  const { boardSize } = settings;

  // const [positionIndex, setPositionIndex] = useState(0);

  const [moveIndex, setMoveIndex] = useState(0);
  const [lineIndex, setLineIndex] = useState(0);
  const [maxMoveIndex, setMaxMoveIndex] = useState(0);
  const [moveStatus, setMoveStatus] = useState<"correct" | "incorrect" | null>(
    null,
  );
  const [playerMovesMade, setPlayerMovesMade] = useState(moveIndex / 2);

  const [isManualNavigation, setIsManualNavigation] = useState(false);
  const [completedLines, setCompletedLines] = useState<number[]>([]);
  const [completedChallengeLines, setCompletedChallengeLines] = useState<
    number[]
  >([]);
  const [practiceLineProgress, setPracticeLineProgress] = useState<number[]>(
    [],
  );
  const [challengeLineProgress, setChallengeLineProgress] = useState<number[]>(
    [],
  );

  const [moves, setMoves] = useState<any[]>([]);
  const [possibleMovesArr, setPossibleMovesArr] = useState([]);
  const [fen, setFen] = useState(() => new Chess().fen());
  const [lastMove, setLastMove] = useState(null);
  // const positionIndex = useRef(0);
  const [lessonState, setLessonState] = useState(LessonState.OPENING);
  const [startMode, setStartMode] = useState<StartMode>("practice");
  const [highLightKey, setHighLightKey] = useState(null);
  const [boardAnimate, setBoardAnimate] = useState(true);
  const persistQueueRef = useRef<Promise<void>>(Promise.resolve());

  const chess = useRef(new Chess()).current;

  const isChallengeLessonState =
    lessonState === LessonState.CHALLENGE ||
    lessonState === LessonState.CHALLENGE_DONE;
  const normalizedChallengeLines = useMemo(() => {
    const practiceLines = Array.isArray(lines) ? lines : [];
    const configuredChallengeLines = Array.isArray(challengeLines)
      ? challengeLines
      : [];

    return buildAlignedChallengeLines(practiceLines, configuredChallengeLines);
  }, [lines, challengeLines]);
  const currentLines = isChallengeLessonState
    ? normalizedChallengeLines
    : lines;
  const line = currentLines?.[lineIndex];
  const maxLines = currentLines?.length || 0;
  const hasChallenge = normalizedChallengeLines.length > 0;
  const isOpening = lessonState === LessonState.OPENING;
  const isPracticeInProgress = lessonState === LessonState.IN_PROGRESS;
  const isChallengeInProgress = lessonState === LessonState.CHALLENGE;
  const isInProgressState = isPracticeInProgress || isChallengeInProgress;
  const showProgress = isInProgressState;
  const practiceLineCount = lines?.length ?? 0;
  const challengeLineCount = normalizedChallengeLines.length;
  const selectedStartLinesLength =
    startMode === "challenge" && hasChallenge
      ? challengeLineCount
      : practiceLineCount;
  const lineCountTotal = isOpening ? selectedStartLinesLength : maxLines;
  const safeLineIndex =
    lineCountTotal > 0 ? Math.min(lineIndex, lineCountTotal - 1) : 0;
  const safePracticeLineIndex =
    practiceLineCount > 0 ? Math.min(lineIndex, practiceLineCount - 1) : 0;
  const safeChallengeLineIndex =
    challengeLineCount > 0 ? Math.min(lineIndex, challengeLineCount - 1) : 0;

  const commentIndex = playingWhite
    ? Math.ceil(moveIndex / 2)
    : Math.floor(moveIndex / 2);
  const ctaButtonClassName = "h-12 text-base font-semibold";
  const secondaryCtaButtonClassName =
    "";
  const disabledSecondaryCtaButtonClassName =
    "disabled:opacity-100 disabled:bg-athens-gray-200 disabled:text-athens-gray-500 disabled:border-athens-gray-300";
  const practiceLineMoveTotals = useMemo(
    () => getLinePlayerMoveTotals(lines, playingWhite),
    [lines, playingWhite],
  );
  const challengeLineMoveTotals = useMemo(
    () => getLinePlayerMoveTotals(normalizedChallengeLines, playingWhite),
    [normalizedChallengeLines, playingWhite],
  );
  const currentLineTotalMoves = Math.max(
    lessonState === LessonState.CHALLENGE
      ? (challengeLineMoveTotals[lineIndex] ?? 0)
      : (practiceLineMoveTotals[lineIndex] ?? 0),
    1,
  );
  const practiceSavedMoves = Math.min(
    practiceLineProgress[safePracticeLineIndex] ?? 0,
    practiceLineMoveTotals[safePracticeLineIndex] ?? 0,
  );
  const practiceLineTotalMoves =
    practiceLineMoveTotals[safePracticeLineIndex] ?? 0;
  const hasResumablePracticeProgress =
    practiceSavedMoves > 0 && practiceSavedMoves < practiceLineTotalMoves;
  const challengeSavedMoves = Math.min(
    challengeLineProgress[safeChallengeLineIndex] ?? 0,
    challengeLineMoveTotals[safeChallengeLineIndex] ?? 0,
  );
  const challengeLineTotalMoves =
    challengeLineMoveTotals[safeChallengeLineIndex] ?? 0;
  const hasResumableChallengeProgress =
    challengeSavedMoves > 0 && challengeSavedMoves < challengeLineTotalMoves;
  const hasCompletedPracticeLine =
    completedLines.length > 0 ||
    practiceLineMoveTotals.some((lineTotal, lineIdx) => {
      if (lineTotal <= 0) {
        return false;
      }
      return (practiceLineProgress[lineIdx] ?? 0) >= lineTotal;
    });
  const effectiveCompletedPracticeLines = useMemo(() => {
    if (lessonState !== LessonState.DONE) {
      return completedLines;
    }

    return Array.from(new Set([...completedLines, lineIndex]));
  }, [lessonState, completedLines, lineIndex]);
  const effectiveCompletedChallengeLines = useMemo(() => {
    if (lessonState !== LessonState.CHALLENGE_DONE) {
      return completedChallengeLines;
    }

    return Array.from(new Set([...completedChallengeLines, lineIndex]));
  }, [lessonState, completedChallengeLines, lineIndex]);
  const effectivePracticeLineProgress = useMemo(() => {
    if (lessonState !== LessonState.DONE) {
      return practiceLineProgress;
    }

    const completedMoves = practiceLineMoveTotals[lineIndex] ?? 0;
    return setLineProgressAtIndex(
      practiceLineProgress,
      lineIndex,
      completedMoves,
    );
  }, [lessonState, practiceLineProgress, lineIndex, practiceLineMoveTotals]);
  const effectiveChallengeLineProgress = useMemo(() => {
    if (lessonState !== LessonState.CHALLENGE_DONE) {
      return challengeLineProgress;
    }

    const completedMoves = challengeLineMoveTotals[lineIndex] ?? 0;
    return setLineProgressAtIndex(
      challengeLineProgress,
      lineIndex,
      completedMoves,
    );
  }, [lessonState, challengeLineProgress, lineIndex, challengeLineMoveTotals]);
  const allPracticeLinesCompleted = useMemo(
    () =>
      areAllLinesCompleted(
        effectivePracticeLineProgress,
        effectiveCompletedPracticeLines,
        practiceLineMoveTotals,
      ),
    [
      effectivePracticeLineProgress,
      effectiveCompletedPracticeLines,
      practiceLineMoveTotals,
    ],
  );
  const allChallengeLinesCompleted = useMemo(
    () =>
      areAllLinesCompleted(
        effectiveChallengeLineProgress,
        effectiveCompletedChallengeLines,
        challengeLineMoveTotals,
      ),
    [
      effectiveChallengeLineProgress,
      effectiveCompletedChallengeLines,
      challengeLineMoveTotals,
    ],
  );
  const isChallengeUnlocked = !hasChallenge || hasCompletedPracticeLine;

  let comment = line?.comments?.[commentIndex];

  const isWhiteTurn = moveIndex % 2 === 0;
  const opponentTurn =
    (playingWhite && !isWhiteTurn) || (!playingWhite && isWhiteTurn);
  const lessonPanelStyle = {
    "--lesson-panel-height": `${(boardSize ?? 0) + 44}px`,
  } as CSSProperties & { "--lesson-panel-height": string };

  if (opponentTurn) {
    comment = isWhiteTurn ? t("White moves.") : t("Black moves.");
  }
  if (lessonState === LessonState.OPENING) {
    comment = introText;
  }

  // Allow moving any legal piece on the player's turn.
  const possibleMoves =
    lessonState === LessonState.OPENING
      ? {}
      : possibleMovesArr[moveIndex] || {};

  // console.log("moveIndex", moveIndex);
  // console.log("possibleMovesArr", possibleMovesArr);
  // console.log("possibleMoves", possibleMoves);

  //console.log("positionIndex", positionIndex);
  // console.log("maxMoveIndex", maxMoveIndex);

  async function persistProgress({
    completedLinesOverride = completedLines,
    completedChallengeLinesOverride = completedChallengeLines,
    practiceLineProgressOverride = practiceLineProgress,
    challengeLineProgressOverride = challengeLineProgress,
    challengeCompletedOverride,
  }: PersistProgressArgs = {}) {
    if (!lessonId) {
      return;
    }

    const normalizedPracticeLineProgress = applyCompletedLinesToProgress(
      normalizeLineProgress(practiceLineProgressOverride),
      normalizeCompletedIndices(completedLinesOverride),
      practiceLineMoveTotals,
    );
    const normalizedChallengeLineProgress = applyCompletedLinesToProgress(
      normalizeLineProgress(challengeLineProgressOverride),
      normalizeCompletedIndices(completedChallengeLinesOverride),
      challengeLineMoveTotals,
    );

    const completedPracticeLineSet = new Set(
      normalizeCompletedIndices(completedLinesOverride),
    );
    const completedChallengeLineSet = new Set(
      normalizeCompletedIndices(completedChallengeLinesOverride),
    );

    normalizedPracticeLineProgress.forEach((movesDone, lineIdx) => {
      const lineTotal = practiceLineMoveTotals[lineIdx] ?? 0;
      if (lineTotal > 0 && movesDone >= lineTotal) {
        completedPracticeLineSet.add(lineIdx);
      }
    });

    normalizedChallengeLineProgress.forEach((movesDone, lineIdx) => {
      const lineTotal = challengeLineMoveTotals[lineIdx] ?? 0;
      if (lineTotal > 0 && movesDone >= lineTotal) {
        completedChallengeLineSet.add(lineIdx);
      }
    });

    const normalizedCompletedLines = Array.from(completedPracticeLineSet).sort(
      (a, b) => a - b,
    );
    const normalizedCompletedChallengeLines = Array.from(
      completedChallengeLineSet,
    ).sort((a, b) => a - b);

    const challengeCompleted =
      challengeCompletedOverride ??
      (hasChallenge &&
        findNextIncompleteLine(
          normalizedChallengeLineProgress,
          challengeLineMoveTotals,
        ) === -1);

    const payload = {
      lessonId,
      completedLines: normalizedCompletedLines,
      completedChallengeLines: normalizedCompletedChallengeLines,
      practiceLineProgress: normalizedPracticeLineProgress,
      challengeLineProgress: normalizedChallengeLineProgress,
      challengeCompleted,
    };

    persistQueueRef.current = persistQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          if (user.memberLoggedIn) {
            await api.post("/lesson-progress", payload);
          } else {
            saveGuestLessonProgress({
              lessonId,
              completedLines: normalizedCompletedLines,
              completedChallengeLines: normalizedCompletedChallengeLines,
              practiceLineProgress: normalizedPracticeLineProgress,
              challengeLineProgress: normalizedChallengeLineProgress,
              challengeCompleted: Boolean(challengeCompleted),
            });
          }

          setCompletedLines(normalizedCompletedLines);
          setCompletedChallengeLines(normalizedCompletedChallengeLines);
          setPracticeLineProgress(normalizedPracticeLineProgress);
          setChallengeLineProgress(normalizedChallengeLineProgress);
        } catch (error) {
          console.error("Failed to save lesson progress:", error);
        }
      });

    await persistQueueRef.current;
  }

  async function loadProgress() {
    if (!lessonId) {
      return;
    }

    try {
      const progress = user.memberLoggedIn
        ? ((await api.get(`/lesson-progress/${lessonId}`))
            .data as LessonProgressResponse)
        : (getGuestLessonProgress(lessonId) as LessonProgressResponse | null);

      if (progress) {
        const parsedCompletedLines = normalizeCompletedIndices(
          progress.completedLines,
        );
        const parsedCompletedChallengeLines = normalizeCompletedIndices(
          progress.completedChallengeLines,
        );
        const parsedPracticeLineProgress = normalizeLineProgress(
          progress.practiceLineProgress,
        );
        const parsedChallengeLineProgress = normalizeLineProgress(
          progress.challengeLineProgress,
        );

        const hydratedPracticeLineProgress = applyCompletedLinesToProgress(
          parsedPracticeLineProgress,
          parsedCompletedLines,
          practiceLineMoveTotals,
        );
        const hydratedChallengeLineProgress = applyCompletedLinesToProgress(
          parsedChallengeLineProgress,
          parsedCompletedChallengeLines,
          challengeLineMoveTotals,
        );

        setCompletedLines(parsedCompletedLines);
        setCompletedChallengeLines(parsedCompletedChallengeLines);
        setPracticeLineProgress(hydratedPracticeLineProgress);
        setChallengeLineProgress(hydratedChallengeLineProgress);

        const nextPracticeLine = findNextIncompleteLine(
          hydratedPracticeLineProgress,
          practiceLineMoveTotals,
        );
        const practiceCompleted = nextPracticeLine === -1;
        const nextChallengeLine = findNextIncompleteLine(
          hydratedChallengeLineProgress,
          challengeLineMoveTotals,
        );
        const challengeCompleted = nextChallengeLine === -1;

        if (practiceCompleted && hasChallenge && !challengeCompleted) {
          setStartMode("challenge");
          setLineIndex(nextChallengeLine === -1 ? 0 : nextChallengeLine);
        } else {
          setStartMode("practice");
          setLineIndex(nextPracticeLine === -1 ? 0 : nextPracticeLine);
        }

        setPlayerMovesMade(0);
        setLessonState(LessonState.OPENING);
      }
    } catch (error) {
      console.error("Failed to load lesson progress:", error);
    }
  }

  async function saveProgress() {
    const updatedPracticeLineProgress = [...practiceLineProgress];
    const updatedChallengeLineProgress = [...challengeLineProgress];
    const updatedCompletedLines = [...completedLines];
    const updatedCompletedChallengeLines = [...completedChallengeLines];

    if (lessonState === LessonState.DONE) {
      const lineTotal = practiceLineMoveTotals[lineIndex] ?? 0;
      if (lineTotal > 0) {
        updatedPracticeLineProgress[lineIndex] = Math.max(
          updatedPracticeLineProgress[lineIndex] ?? 0,
          lineTotal,
        );
      }
      updatedCompletedLines.push(lineIndex);
    } else if (lessonState === LessonState.CHALLENGE_DONE) {
      const lineTotal = challengeLineMoveTotals[lineIndex] ?? 0;
      if (lineTotal > 0) {
        updatedChallengeLineProgress[lineIndex] = Math.max(
          updatedChallengeLineProgress[lineIndex] ?? 0,
          lineTotal,
        );
      }
      updatedCompletedChallengeLines.push(lineIndex);
    }

    await persistProgress({
      completedLinesOverride: updatedCompletedLines,
      completedChallengeLinesOverride: updatedCompletedChallengeLines,
      practiceLineProgressOverride: updatedPracticeLineProgress,
      challengeLineProgressOverride: updatedChallengeLineProgress,
    });
  }

  function initLine() {
    const chess = new Chess();
    const currentLines =
      lessonState === LessonState.CHALLENGE ? normalizedChallengeLines : lines;
    const line = currentLines?.[lineIndex];
    if (!line) return;
    chess.loadPgn(line.moves);
    const moves = chess.history({ verbose: true });

    chess.reset();

    // For challenge mode, load the challenge FEN if it exists.
    // For practice mode, always start from the regular starting position.
    if (lessonState === LessonState.CHALLENGE && challengeFen) {
      chess.load(challengeFen);
    }

    const possibleMovesArr = [];

    possibleMovesArr.push(convertMoves(chess.moves({ verbose: true })));

    moves.forEach((move, index) => {
      chess.move(move);
      let possibleMoves;
      if (playingWhite) {
        possibleMoves =
          index % 2 === 1 ? convertMoves(chess.moves({ verbose: true })) : {};
      } else {
        possibleMoves =
          index % 2 === 1 ? {} : convertMoves(chess.moves({ verbose: true }));
      }
      possibleMovesArr.push(possibleMoves);
    });

    setMoves(moves);
    setPossibleMovesArr(possibleMovesArr);
  }

  function startLesson(targetLineIndex = lineIndex, resumePlayerMoves = 0) {
    setBoardAnimate(false);
    chess.reset();
    const lineTotal = practiceLineMoveTotals[targetLineIndex] ?? 0;
    const clampedPlayerMoves = Math.max(
      0,
      Math.min(resumePlayerMoves, lineTotal),
    );
    const startingMoveIndex = getMoveIndexForResume(
      playingWhite,
      clampedPlayerMoves,
    );

    setFen(chess.fen());
    setLastMove(null);
    setMoves([]);
    setPossibleMovesArr([]);
    setMoveIndex(startingMoveIndex);
    setMaxMoveIndex(startingMoveIndex);
    setMoveStatus(null);
    setPlayerMovesMade(clampedPlayerMoves);
    setLineIndex(targetLineIndex);
    setStartMode("practice");
    setLessonState(LessonState.IN_PROGRESS);
  }

  function startChallenge(targetLineIndex = lineIndex, resumePlayerMoves = 0) {
    if (normalizedChallengeLines.length === 0) {
      console.error("No challenge lines defined");
      return;
    }
    setBoardAnimate(false);
    const lineTotal = challengeLineMoveTotals[targetLineIndex] ?? 0;
    const clampedPlayerMoves = Math.max(
      0,
      Math.min(resumePlayerMoves, lineTotal),
    );
    const startingMoveIndex = getMoveIndexForResume(
      playingWhite,
      clampedPlayerMoves,
    );

    chess.reset();
    if (challengeFen) {
      chess.load(challengeFen);
    }

    setFen(chess.fen());
    setLastMove(
      challengeOpeningLastMove ? uci2move(challengeOpeningLastMove) : null,
    );
    setMoveIndex(startingMoveIndex);
    setMaxMoveIndex(startingMoveIndex);
    setLineIndex(targetLineIndex);
    setMoves([]);
    setPossibleMovesArr([]);
    setMoveStatus(null);
    setPlayerMovesMade(clampedPlayerMoves);
    setStartMode("challenge");
    setLessonState(LessonState.CHALLENGE);
  }

  function resetToOpening(nextStartMode: StartMode) {
    setBoardAnimate(false);
    chess.reset();
    setStartMode(nextStartMode);
    setLessonState(LessonState.OPENING);
    setFen(chess.fen());
    setLastMove(null);
    setMoves([]);
    setPossibleMovesArr([]);
    setMoveIndex(0);
    setMaxMoveIndex(0);
    setMoveStatus(null);
    setPlayerMovesMade(0);
    setIsManualNavigation(false);
  }

  function getResumableMovesForLine(
    mode: StartMode,
    targetLineIndex: number,
  ): number {
    const isChallengeMode = mode === "challenge" && hasChallenge;
    const lineProgress = isChallengeMode
      ? challengeLineProgress
      : practiceLineProgress;
    const lineMoveTotals = isChallengeMode
      ? challengeLineMoveTotals
      : practiceLineMoveTotals;
    const lineTotal = lineMoveTotals[targetLineIndex] ?? 0;
    const savedMoves = Math.min(lineProgress[targetLineIndex] ?? 0, lineTotal);

    if (savedMoves <= 0 || savedMoves >= lineTotal) {
      return 0;
    }

    return savedMoves;
  }

  function changeLine(nextLineIndex: number) {
    if (lineCountTotal <= 0) {
      return;
    }

    const clampedLineIndex = Math.max(
      0,
      Math.min(nextLineIndex, lineCountTotal - 1),
    );

    if (clampedLineIndex === lineIndex) {
      return;
    }

    setLineIndex(clampedLineIndex);
    resetToOpening(startMode);
  }

  function startPracticeFromOpening() {
    const resumePlayerMoves = getResumableMovesForLine(
      "practice",
      safePracticeLineIndex,
    );
    startLesson(safePracticeLineIndex, resumePlayerMoves);
  }

  function restartPracticeFromOpening() {
    startLesson(safePracticeLineIndex, 0);
  }

  function startChallengeFromOpening() {
    if (!hasChallenge || !isChallengeUnlocked) {
      return;
    }

    const resumePlayerMoves = getResumableMovesForLine(
      "challenge",
      safeChallengeLineIndex,
    );
    startChallenge(safeChallengeLineIndex, resumePlayerMoves);
  }

  function restartChallengeFromOpening() {
    if (!hasChallenge || !isChallengeUnlocked) {
      return;
    }

    startChallenge(safeChallengeLineIndex, 0);
  }

  function getNextUncompletedChallengeLine() {
    const nextChallengeLine = findNextIncompleteLine(
      challengeLineProgress,
      challengeLineMoveTotals,
    );
    return nextChallengeLine === -1 ? 0 : nextChallengeLine;
  }

  // function updatePositionIndex(index: number) {
  //   positionIndex.current = index;

  //   const move = moves[positionIndex.current];
  //   // move && setFen(move.before);
  //   setMoveIndex(positionIndex.current);
  // }

  // useEffect(() => {
  //   positionIndex.current = moveIndex;
  // }, [moveIndex]);

  useEffect(() => {
    loadProgress();
  }, [user.memberLoggedIn, lessonId]);

  useEffect(() => {
    initLine();
  }, [lineIndex, lessonState]);

  useEffect(() => {
    if (lineCountTotal <= 0) {
      return;
    }

    if (lineIndex >= lineCountTotal) {
      setLineIndex(lineCountTotal - 1);
    }
  }, [lineCountTotal, lineIndex]);

  useEffect(() => {
    if (boardAnimate) {
      return;
    }

    const timer = setTimeout(() => {
      setBoardAnimate(true);
    }, 0);

    return () => clearTimeout(timer);
  }, [boardAnimate]);

  useEffect(() => {
    if (lessonState === LessonState.OPENING) return;
    if (!moves || moves.length === 0) return;

    // Show position after the previous move (includes castling with both king and rook)
    const fen =
      moveIndex === 0
        ? lessonState === LessonState.CHALLENGE && challengeFen
          ? challengeFen
          : moves[0]?.before
        : moves[moveIndex - 1]?.after;

    const move = moves?.[moveIndex - 1];
    if (fen) {
      setFen(fen);
    }
    setLastMove(move);
  }, [moveIndex, moves, lessonState, challengeFen]);

  useEffect(() => {
    if (
      lessonState === LessonState.DONE ||
      lessonState === LessonState.CHALLENGE_DONE
    ) {
      saveProgress();
    }
  }, [lessonState]);

  // Auto-play opponent moves
  useEffect(() => {
    if (
      lessonState !== LessonState.IN_PROGRESS &&
      lessonState !== LessonState.CHALLENGE
    )
      return;
    if (!moves || moves.length === 0) return;
    if (moveStatus !== null) return; // Don't auto-play during feedback
    if (isManualNavigation) return; // Don't auto-play during manual navigation

    const isWhiteTurn = moveIndex % 2 === 0;
    const opponentTurn =
      (playingWhite && !isWhiteTurn) || (!playingWhite && isWhiteTurn);

    if (opponentTurn && moveIndex < moves.length) {
      const timer = setTimeout(() => {
        setMoveIndex(moveIndex + 1);
        setMaxMoveIndex(moveIndex + 1);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [
    moveIndex,
    lessonState,
    moves,
    playingWhite,
    moveStatus,
    isManualNavigation,
  ]);

  function moveMade(move) {
    if (!move) {
      console.error("moveMade called with undefined move");
      return;
    }
    if (!moves || moves.length === 0) {
      return;
    }
    setHighLightKey(null);
    setIsManualNavigation(false);

    const correctMove = moves[moveIndex];

    if (!correctMove) {
      return;
    }

    if (
      move.from === correctMove.from &&
      move.to === correctMove.to &&
      move.promotion === correctMove.promotion
    ) {
      if (isChallengeInProgress) {
        setMoveStatus("correct");
      } else {
        setMoveStatus(null);
      }
      const nextPlayerMovesMade = Math.ceil((moveIndex + 1) / 2);
      setPlayerMovesMade(nextPlayerMovesMade);

      if (lessonState === LessonState.CHALLENGE) {
        const updatedChallengeLineProgress = setLineProgressAtIndex(
          challengeLineProgress,
          lineIndex,
          nextPlayerMovesMade,
        );
        setChallengeLineProgress(updatedChallengeLineProgress);
        void persistProgress({
          challengeLineProgressOverride: updatedChallengeLineProgress,
        });
      } else if (lessonState === LessonState.IN_PROGRESS) {
        const updatedPracticeLineProgress = setLineProgressAtIndex(
          practiceLineProgress,
          lineIndex,
          nextPlayerMovesMade,
        );
        setPracticeLineProgress(updatedPracticeLineProgress);
        void persistProgress({
          practiceLineProgressOverride: updatedPracticeLineProgress,
        });
      }

      if (moves.length === moveIndex + 1) {
        const newState =
          lessonState === LessonState.CHALLENGE
            ? LessonState.CHALLENGE_DONE
            : LessonState.DONE;
        setLessonState(newState);
        return;
      } else {
        // Increment moveIndex to show your move
        setMoveIndex(moveIndex + 1);
        setMaxMoveIndex(moveIndex + 1);

        if (isChallengeInProgress) {
          setTimeout(() => {
            setMoveStatus(null);
          }, 800);
        }
      }
    } else {
      setMoveStatus("incorrect");

      // Don't set FEN here - let the useEffect handle it
      // Just trigger a re-render and clear the error after delay
      setTimeout(() => {
        setFen(""); // Clear FEN first
        setTimeout(() => {
          // Then set correct FEN after a brief delay
          const correctFen =
            moveIndex === 0
              ? lessonState === LessonState.CHALLENGE && challengeFen
                ? challengeFen
                : moves[0]?.before
              : moves[moveIndex - 1]?.after;
          setFen(correctFen);
          setMoveStatus(null);
        }, 10);
      }, 500);
    }
  }

  function renderChallengeFeedbackCard() {
    if (!isChallengeInProgress) {
      return null;
    }

    const isCorrectMove = moveStatus === "correct";
    const isIncorrectMove = moveStatus === "incorrect";
    const feedbackTitle = isCorrectMove
      ? t("Best move")
      : isIncorrectMove
        ? t("Incorrect move")
        : t("Your turn");
    const feedbackText = isCorrectMove
      ? t("Keep going!")
      : isIncorrectMove
        ? t("Try again")
        : t("Play the {{opening}}", { opening: title });

    return (
      <div className="flex items-start w-full pb-2 mx-auto md:pb-4">
        <div className="w-full p-3 bg-white rounded-lg md:p-4">
          <div className="pb-1 text-base font-semibold md:pb-2">
            <span className="flex gap-1.5 items-center">
              <span>{feedbackTitle}</span>
            </span>
          </div>
          <div className="text-sm text-gray-700">{feedbackText}</div>
        </div>
      </div>
    );
  }

  function renderCommentBubble() {
    if (isChallengeInProgress || lessonState === LessonState.CHALLENGE_DONE) {
      return null;
    }

    return (
      <div className="flex items-start w-full pb-2 mx-auto md:pb-4">
        <div className="overflow-auto p-3 w-full max-h-32 leading-normal text-[15px] text-gray-900 rounded-lg bg-white md:max-h-none md:p-4">
          {comment}
        </div>
      </div>
    );
  }

  function renderProgressBar() {
    if (!showProgress) {
      return null;
    }

    const progressPercent =
      currentLineTotalMoves > 0
        ? (playerMovesMade / currentLineTotalMoves) * 100
        : 0;

    return (
      <>
        <div className="mb-1 text-xs text-gray-600">
          {/* {lessonState === LessonState.CHALLENGE ? t("Challenge ") : t("Practice ")} */}
          {t("Moves")} {playerMovesMade}/{currentLineTotalMoves}
        </div>
        <LessonProgressBar
          className="mb-2 md:mb-4"
          value={progressPercent}
        />
      </>
    );
  }

  function renderOpeningActions() {
    const practiceCtaText = hasResumablePracticeProgress
      ? t("Resume practice")
      : t("Start practice");
    const challengeCtaText = hasResumableChallengeProgress
      ? t("Resume Challenge")
      : t("Start Challenge");

    return (
      <div className="flex flex-col items-center justify-center w-full gap-2 mt-2 md:gap-3 md:mt-3">
        <div className="w-full">
          <Button
            className={ctaButtonClassName}
            onClick={startPracticeFromOpening}
            size="full_lg"
          >
            {practiceCtaText}
          </Button>
          {hasResumablePracticeProgress && (
            <button
              type="button"
              className="mx-auto mt-1 flex w-fit items-center gap-1 py-[2px] text-sm font-normal text-athens-gray-600 hover:text-orange-400"
              onClick={restartPracticeFromOpening}
            >
              <span aria-hidden="true" className="text-base leading-none">
                ↺
              </span>
              {t("Restart practice")}
            </button>
          )}
        </div>
        {hasChallenge &&
          (isChallengeUnlocked ? (
            <div className="w-full">
              <Button
                variant="neutral"
                className={cn("text-base", secondaryCtaButtonClassName)}
                onClick={startChallengeFromOpening}
                size="full_lg"
              >
                {challengeCtaText}
              </Button>
              {hasResumableChallengeProgress && (
                <button
                  type="button"
                  className="mx-auto mt-1 flex w-fit items-center gap-1 py-[2px] text-sm font-normal text-athens-gray-600 hover:text-orange-400"
                  onClick={restartChallengeFromOpening}
                >
                  <span aria-hidden="true" className="text-base leading-none">
                    ↺
                  </span>
                  {t("Restart challenge")}
                </button>
              )}
            </div>
          ) : (
            <div className="w-full">
              <Button
                variant="neutral"
                className={cn(
                  "text-base",
                  secondaryCtaButtonClassName,
                  disabledSecondaryCtaButtonClassName,
                )}
                disabled={true}
                size="full_lg"
              >
                <span className="inline-flex items-center gap-1.5">
                  <FaLock className="text-xs" aria-hidden="true" />
                  {challengeCtaText}
                </span>
              </Button>
            </div>
          ))}
      </div>
    );
  }

  function renderLineSelector(buttonClassName: string, align: "start" | "end") {
    if (lineCountTotal <= 0) {
      return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={buttonClassName}>
            <span>
              {t("Line")} {safeLineIndex + 1}/{lineCountTotal}
            </span>
            <FaChevronDown className="text-[11px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className="overflow-y-auto max-h-64 min-w-[9rem]"
        >
          {Array.from({ length: lineCountTotal }, (_, optionIndex) => (
            <DropdownMenuItem
              key={`line-${optionIndex}`}
              className={cn(
                optionIndex === safeLineIndex &&
                  "bg-athens-gray-100 font-semibold text-altblue-500",
              )}
              onSelect={() => changeLine(optionIndex)}
            >
              {t("Line")} {optionIndex + 1}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  function renderInProgressActions() {
    const showStartChallengeFromPractice =
      isPracticeInProgress && startMode === "challenge" && hasChallenge;

    return (
      <div className="flex flex-col items-center w-full gap-2 md:gap-3">
        {showStartChallengeFromPractice && (
          <Button
            variant="neutral"
            className={ctaButtonClassName}
            onClick={() => {
              startChallenge(getNextUncompletedChallengeLine());
            }}
            size="full_lg"
          >
            {t("Start Challenge")}
          </Button>
        )}

        <div className="flex items-center justify-between w-full gap-1">
          <Button
            variant="neutral"
            size="sm"

            onClick={() => resetToOpening(startMode)}
          >
            {t("Back")}
          </Button>
          <div className="flex gap-2">
            <Button
              variant="neutral"
              size="sm"

              onClick={() => {
                const key = moves[moveIndex]?.from;
                if (key) {
                  setHighLightKey(key);
                }
              }}
            >
              {t("Hint")}
            </Button>
            <Button
              variant="quiet"

              disabled={moveIndex === 0}
              size="sm"
              onClick={() => {
                if (moveIndex > 0) {
                  setIsManualNavigation(true);
                  setMoveStatus(null);
                  setMoveIndex((currentMoveIndex) => currentMoveIndex - 1);
                }
              }}
            >
              <FaStepBackward />
            </Button>

            <Button
              variant="quiet"

              disabled={moveIndex === maxMoveIndex}
              size="sm"
              onClick={() => {
                if (moveIndex < maxMoveIndex) {
                  setIsManualNavigation(true);
                  setMoveStatus(null);
                  setMoveIndex((currentMoveIndex) => currentMoveIndex + 1);
                }
              }}
            >
              <FaStepForward />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderDoneActions() {
    if (lineIndex < maxLines - 1) {
      return (
        <div className="flex flex-col w-full gap-2 md:gap-3">
          <Button
            className={ctaButtonClassName}
            onClick={() => {
              startLesson(lineIndex + 1);
            }}
            size="full_lg"
          >
            {t("Start next line")}
          </Button>
          {hasChallenge && (
            <Button
              variant="neutral"
              onClick={() => {
                startChallenge(getNextUncompletedChallengeLine());
              }}
              size="full_lg"
            >
              {t("Start Challenge")}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col w-full gap-2 md:gap-3">
        <Button
          className={ctaButtonClassName}
          onClick={() => {
            startLesson(0);
          }}
          size="full_lg"
        >
          {t("Restart")}
        </Button>
        {hasChallenge && (
          <Button
            variant="neutral"
            size="full_lg"
            onClick={() => {
              startChallenge(getNextUncompletedChallengeLine());
            }}
          >
            {t("Start Challenge")}
          </Button>
        )}
      </div>
    );
  }

  function renderChallengeDoneActions() {
    if (lineIndex < maxLines - 1) {
      return (
        <Button
          className={ctaButtonClassName}
          onClick={() => {
            startChallenge(lineIndex + 1);
          }}
          size="full_lg"
        >
          {t("Next challenge line")}
        </Button>
      );
    }

    return (
      <div className="flex flex-col w-full gap-2 md:gap-3">
        <Button
          className={ctaButtonClassName}
          onClick={() => {
            startChallenge(0);
          }}
          size="full_lg"
        >
          {t("Retry Challenge")}
        </Button>
        <Link href="/learn/openings">
          <Button
            className={ctaButtonClassName}
            variant="neutral"
            size="full_lg"
          >
            {t("Back to Lessons")}
          </Button>
        </Link>
      </div>
    );
  }

  function renderStateActions() {
    if (isOpening) {
      return renderOpeningActions();
    }

    if (isInProgressState) {
      return renderInProgressActions();
    }

    return null;
  }

  return (
    <div className="flex flex-col items-center md:mt-4">
      <div className="flex flex-col items-center w-full md:w-auto md:flex-row md:items-start">
        <div className="flex justify-center">
          <Board
            lastMove={lastMove}
            settings={settings}
            fen={fen}
            animate={boardAnimate}
            autoCastle
            size={boardSize}
            playingWhite={playingWhite}
            possibleMoves={possibleMoves}
            onMoveMade={(move) => moveMade(move)}
            highLightKey={highLightKey}
          />
        </div>

        <div
          className={
            "flex flex-col mt-2 w-full text-sm rounded-md md:mt-0 md:ml-4 md:h-[var(--lesson-panel-height)] md:w-[400px] md:bg-athens-gray-150"
          }
          style={lessonPanelStyle}
        >
          <div className="hidden items-center gap-2 p-3 px-5 text-sm font-bold rounded-t-md md:flex text-athens-gray-50 bg-altblue-500">
            <div className="flex min-w-0 items-center gap-2">
              <Link
                href={"/learn/openings"}
                className="flex items-center justify-center p-1 transition-colors rounded hover:bg-altblue-600 hover:text-athens-gray-50"
                aria-label={t("Back to Lessons")}
              >
                <FaArrowLeft className="text-sm" />
              </Link>
              <span className="truncate">{title}</span>
              {renderLineSelector(
                "inline-flex items-center gap-1 px-2 py-1 text-sm font-normal transition-colors rounded hover:bg-altblue-600",
                "end",
              )}
            </div>
          </div>
          <div className="flex flex-col flex-grow p-2 w-full md:h-full md:w-[400px] md:p-3 md:py-3">
            <div className="md:flex-1 md:min-h-0 md:overflow-y-auto">
              <div className="flex flex-col w-full gap-0.5 pt-1 pb-1 text-center md:hidden">
                <span className="text-base font-semibold">{title}</span>
                <div className="flex justify-center">
                  {renderLineSelector(
                    "inline-flex items-center gap-1 px-2 py-1 text-sm font-normal text-gray-600 transition-colors rounded hover:bg-athens-gray-200",
                    "start",
                  )}
                </div>
              </div>

              {renderChallengeFeedbackCard() || renderCommentBubble()}
            </div>

            <div className="flex flex-col items-start w-full pt-1 pb-2 mx-auto md:pt-2 md:pb-4">
              {lessonState === LessonState.DONE && (
                <div className="flex flex-col items-center justify-center w-full mt-2 md:mt-3">
                  <div className="pb-1 text-base font-semibold md:pb-2">
                    <span className="flex items-center gap-1.5">
                      <MoveIcon moveType={MoveType.GOOD} />{" "}
                      <div>{t("Congratulations!")}</div>
                    </span>
                  </div>
                  <div className="pb-3 text-sm md:pb-6">
                    {allPracticeLinesCompleted
                      ? t("You have completed all the lines")
                      : t("You have completed the line")}
                  </div>
                  {renderDoneActions()}
                </div>
              )}

              {lessonState === LessonState.CHALLENGE_DONE && (
                <div className="flex flex-col items-center justify-center w-full mt-2 md:mt-3">
                  <div className="pb-1 text-base font-semibold md:pb-2">
                    <span className="flex items-center gap-1.5">
                      <FaTrophy /> <div>{t("Challenge Complete!")}</div>
                    </span>
                  </div>
                  <div className="pb-3 text-sm md:pb-6">
                    {allChallengeLinesCompleted
                      ? t("You've mastered all challenge lines!")
                      : t("You've completed this challenge line")}
                  </div>
                  {renderChallengeDoneActions()}
                </div>
              )}

              {renderProgressBar()}
              {lessonState !== LessonState.DONE &&
                lessonState !== LessonState.CHALLENGE_DONE &&
                renderStateActions()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
